import './Playtest.scss';

import * as Yup from 'yup';

import { Button, Form } from 'react-bootstrap';
import {
  CANADA,
  FIRST_QUESTION_ID,
  PLAYTEST_TYPES,
  PROFIL_TABS,
  USA
} from '../../utils/constants';
import {
  checkUserStatus,
  getCandidatePlaytests,
  putCandidatePlaytests,
  selectIsFetchingPlaytests,
  selectPlaytests,
  selectUserStatus
} from '../../ducks/entities';
import {
  scrollToBannerBottom,
  scrollToTop,
  setTitle
} from '../../utils/generic';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Formik } from 'formik';
import LoaderComponent from '../../components/Loader/LoaderComponents';
import MobileTesting from '../../components/MobileTesting/MobileTesting';
import ModalTemplate from '../../components/ModalTemplate/ModalTemplate';
import { Playtest } from '../../models/candidate';
import ProfileSteps from '../../components/ProfileSteps/ProfileSteps';
import RemoteTesting from '../../components/RemoteTesting/RemoteTesting';
import ScrollToFieldError from '../../components/ScrollToFieldError/ScrollToFieldError';
import StudioTesting from '../../components/StudioTesting/StudioTesting';
import ToolsSoftwareTesting from '../../components/ToolsSoftwareTesting/ToolsSoftwareTesting';
import VrTesting from '../../components/VrTesting/VrTesting';
import YesNoButton from '../../components/YesNoButton/YesNoButton';
import iconDots from '../../assets/images/ubisoft-playtest-icon-dots-black.webp';
import imageStep from '../../assets/images/ubisoft-playtest-profile-mobile-steps-2-4.webp';
import messages from './messagesPlaytests';
import profilMessage from './messages';
import { selectAuth } from '../../ducks/auth';
import siteMessages from '../root/siteMessages';
import { toast } from 'react-toastify';
import { useIntl } from 'react-intl';
import { useToggle } from 'react-use';

interface Props {
  setIsFieldModified(value: boolean): void;
}

const SECTION_NAME = 'playtest';

const Playtests = (props: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const playtests = useSelector(selectPlaytests);
  const isFetchingPlaytests = useSelector(selectIsFetchingPlaytests);
  const [next, setNext] = useState(true);
  const navigate = useNavigate();
  const [schema, setSchema] = useState<any>();
  const [remoteTestingSchema, setRemoteTestingSchema] = useState<any>();
  const [studioTestingSchema, setStudioTestingSchema] = useState<any>();
  const [mobileTestingSchema, setMobileTestingSchema] = useState<any>();
  const [toolsSoftwareSchema, setToolsSoftwareSchema] = useState<any>();
  const [showUnsubscribeModal, toggleUnsubscribeModal] = useToggle(false);
  const [receiveEmailsSetting, setReceiveEmailsSetting] = useState<
    boolean | null
  >(null);
  const userStatus = useSelector(selectUserStatus);
  const location = useLocation();
  const [validateOnChange, setValidateOnChange] = useState(false);

  useEffect(() => {
    setTitle(intl.formatMessage({ ...profilMessage.playtests }));
  }, []);

  useEffect(() => {
    dispatch(getCandidatePlaytests());
  }, [auth.userId]);

  useEffect(() => {
    if (playtests) setReceiveEmailsSetting(playtests.receiveEmails);
  }, [playtests]);

  useEffect(() => {
    setSchema({
      receiveEmails: Yup.boolean().required(
        intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
      ),
      remoteTesting: Yup.boolean().required(
        intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
      ),
      candidateRemote: Yup.object().when('remoteTesting', {
        is: true,
        then: () =>
          Yup.object()
            .shape({
              ...remoteTestingSchema
            })
            .required(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })),
        otherwise: () => Yup.object().nullable()
      }),
      studioTesting: Yup.boolean().required(
        intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
      ),
      candidateStudio: Yup.object().when('studioTesting', {
        is: true,
        then: () =>
          Yup.object()
            .shape({
              ...studioTestingSchema
            })
            .required(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })),
        otherwise: () => Yup.object().nullable()
      }),
      mobileTesting: Yup.boolean().required(
        intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
      ),
      candidateMobile: Yup.object().when('mobileTesting', {
        is: true,
        then: () =>
          Yup.object()
            .shape({
              ...mobileTestingSchema
            })
            .required(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })),
        otherwise: () => Yup.object().nullable()
      }),
      vrTesting: Yup.boolean().required(
        intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
      ),

      toolsSoftwareTesting: Yup.boolean().required(
        intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
      ),
      candidateToolsSoftware: Yup.object().when('toolsSoftwareTesting', {
        is: true,
        then: () =>
          Yup.object()
            .shape({
              ...toolsSoftwareSchema
            })
            .required(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })),
        otherwise: () => Yup.object().nullable()
      })
    });
    location.state?.prevRoute ? scrollToBannerBottom() : scrollToTop();
  }, [
    remoteTestingSchema,
    studioTestingSchema,
    mobileTestingSchema,
    toolsSoftwareSchema
  ]);

  const onSubmit = async (values: Playtest, { setSubmitting }: any) => {
    values.playtestTypeIds = [];
    if (values.remoteTesting) {
      values.playtestTypeIds.push(PLAYTEST_TYPES.remote);
    } else {
      values.candidateRemote = null;
    }

    if (
      values.candidateRemote &&
      values.candidateRemote.countryCode !== CANADA &&
      values.candidateRemote.countryCode !== USA
    ) {
      values.candidateRemote.provinceId = undefined;
    }

    if (values.studioTesting) {
      values.playtestTypeIds.push(PLAYTEST_TYPES.studio);
    } else {
      values.candidateStudio = null;
    }

    if (values.mobileTesting) {
      values.playtestTypeIds.push(PLAYTEST_TYPES.mobile);
    } else {
      values.candidateMobile = null;
    }

    if (values.vrTesting) {
      values.playtestTypeIds.push(PLAYTEST_TYPES.vr);
    }

    if (values.toolsSoftwareTesting) {
      values.playtestTypeIds.push(PLAYTEST_TYPES.toolsSoftware);
    } else {
      values.candidateToolsSoftware = null;
    }

    setSubmitting(true);
    props.setIsFieldModified(false);
    await (dispatch(putCandidatePlaytests(values)) as any)
      .then(() => {
        toast.success(
          intl.formatMessage({ ...siteMessages.updateProfileSuccess })
        );
        setSubmitting(false);
        dispatch(checkUserStatus());
        if (next) {
          const index = Object.keys(userStatus).findIndex(
            (k) => !(userStatus as any)[k] && k !== SECTION_NAME
          );
          navigate(
            location.pathname.replace('playtests', PROFIL_TABS[index].pathname),
            { state: { prevRoute: location.pathname }, replace: true }
          );
        }
      })
      .catch(() => {
        props.setIsFieldModified(true);
        toast.error(intl.formatMessage({ ...siteMessages.updateProfileFail }));
      });
  };

  const onSetField = (
    field: string,
    value: any,
    setFieldValue: any,
    shouldValidate?: boolean | undefined
  ) => {
    props.setIsFieldModified(true);
    setFieldValue(field, value, shouldValidate);
  };

  return (
    <div className="playtests">
      <div className="required-field">
        <p>{intl.formatMessage({ ...profilMessage.fieldRequired })}</p>
      </div>
      {!isFetchingPlaytests && playtests ? (
        <Formik
          initialValues={{
            ...playtests,
            remoteTesting: playtests.playtestTypeIds.includes(
              PLAYTEST_TYPES.remote
            ),
            studioTesting: playtests.playtestTypeIds.includes(
              PLAYTEST_TYPES.studio
            ),
            mobileTesting: playtests.playtestTypeIds.includes(
              PLAYTEST_TYPES.mobile
            ),
            vrTesting: playtests.playtestTypeIds.includes(PLAYTEST_TYPES.vr),
            toolsSoftwareTesting: playtests.playtestTypeIds.includes(
              PLAYTEST_TYPES.toolsSoftware
            )
          }}
          validationSchema={Yup.object().shape(schema)}
          onSubmit={onSubmit}
          validateOnChange={validateOnChange}
        >
          {({ values, errors, handleSubmit, isSubmitting, setFieldValue }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <ScrollToFieldError />
              {showUnsubscribeModal && (
                <ModalTemplate
                  onClose={() => {
                    toggleUnsubscribeModal(false);
                  }}
                  text={intl.formatMessage({ ...messages.unsubscribeText })}
                  confirmBtn={
                    <Button
                      variant="danger"
                      onClick={() => {
                        toggleUnsubscribeModal(false);
                        setReceiveEmailsSetting(false);
                        setFieldValue('receiveEmails', false);
                      }}
                    >
                      {intl.formatMessage({ ...messages.unsubscribeConfirm })}
                    </Button>
                  }
                />
              )}
              <div>
                <div className="header" id={FIRST_QUESTION_ID}>
                  <img src={iconDots} alt="" className="mt-n1" />
                  <h3>
                    {intl.formatMessage({ ...messages.headerPlaytestTypes })}
                  </h3>
                </div>
                <div className="content playtest-interest">
                  <YesNoButton
                    id="receiveEmails"
                    name="receiveEmails"
                    label={intl.formatMessage({ ...messages.receiveEmails })}
                    value={receiveEmailsSetting}
                    onChange={(value: any) => {
                      if (value) {
                        setReceiveEmailsSetting(value);
                        setFieldValue('receiveEmails', value);
                      } else if (value != receiveEmailsSetting)
                        toggleUnsubscribeModal(true);
                      props.setIsFieldModified(true);
                    }}
                    error={errors.receiveEmails}
                  />
                </div>
              </div>
              <RemoteTesting
                isInterested={values.remoteTesting}
                values={values.candidateRemote ?? {}}
                errors={errors.candidateRemote}
                setFieldValue={(
                  field: string,
                  value: any,
                  shouldValidate?: boolean | undefined
                ) => onSetField(field, value, setFieldValue, shouldValidate)}
                updateSchema={(schema) => setRemoteTestingSchema(schema)}
              />
              <StudioTesting
                isInterested={values.studioTesting}
                values={values.candidateStudio ?? {}}
                errors={errors.candidateStudio}
                setFieldValue={(
                  field: string,
                  value: any,
                  shouldValidate?: boolean | undefined
                ) => onSetField(field, value, setFieldValue, shouldValidate)}
                updateSchema={(schema) => setStudioTestingSchema(schema)}
              />
              <MobileTesting
                isInterested={values.mobileTesting}
                values={values.candidateMobile ?? {}}
                errors={errors.candidateMobile}
                setFieldValue={(
                  field: string,
                  value: any,
                  shouldValidate?: boolean | undefined
                ) => onSetField(field, value, setFieldValue, shouldValidate)}
                updateSchema={(schema) => setMobileTestingSchema(schema)}
              />
              <VrTesting
                isInterested={values.vrTesting}
                setFieldValue={(
                  field: string,
                  value: any,
                  shouldValidate?: boolean | undefined
                ) => onSetField(field, value, setFieldValue, shouldValidate)}
              />
              <ToolsSoftwareTesting
                isInterested={values.toolsSoftwareTesting}
                values={values.candidateToolsSoftware ?? {}}
                errors={errors.candidateToolsSoftware}
                setFieldValue={(
                  field: string,
                  value: any,
                  shouldValidate?: boolean | undefined
                ) => onSetField(field, value, setFieldValue, shouldValidate)}
                updateSchema={(schema) => setToolsSoftwareSchema(schema)}
              />
              <div className="btns-save">
                <Button
                  variant="primary"
                  disabled={isSubmitting}
                  onClick={() => {
                    !Object.keys(userStatus).find(
                      (k) => !(userStatus as any)[k] && k !== SECTION_NAME
                    )
                      ? setNext(false)
                      : setNext(true);
                    setValidateOnChange(true);
                    handleSubmit();
                  }}
                >
                  <LoaderComponent
                    isLoading={isSubmitting}
                    className="lds-small"
                  >
                    {!Object.keys(userStatus).find(
                      (k) => !(userStatus as any)[k] && k !== SECTION_NAME
                    )
                      ? intl.formatMessage({ ...siteMessages.save })
                      : intl.formatMessage({ ...siteMessages.saveAndNext })}
                  </LoaderComponent>
                </Button>
              </div>
              <ProfileSteps step={2} steps={4} image={imageStep} />
            </Form>
          )}
        </Formik>
      ) : (
        <div className="loading">
          {[...Array(14).keys()].map((i) => (
            <p key={`playtests-${i}`} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Playtests;
